import React, { Dispatch, SetStateAction } from 'react';
import { InputText } from '@/components/form';
import { FieldValues, useForm, UseFormSetValue } from 'react-hook-form';
import Icons from '@/const/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { freepikService, GET_CAMPAIGNS_IMAGES } from '@/api';
import {
	CampaignChannels,
	FreepikImage,
	MarketingImageData,
} from '@/interfaces';
import { useDebounce } from 'use-debounce';
import { ImageComponent } from './ImageComponent';
import { ContainerImage } from './ContainerImage';
import Image from 'next/image';
import clsx from 'clsx';
import { Icon } from '@/components/common';
import { getAspectRatioByChannel, getImageSizeByChannel } from '@/lib';
import useModal from '@/hooks/useModal';
import { ImageEditor } from '@/components/dashboard';

interface Props {
	sourceType: string;
	isCaseReadOnly: boolean;
	srcImg: string | null;
	setValue: UseFormSetValue<FieldValues>;
	setSourceType: Dispatch<SetStateAction<string>>;
	setFile: React.Dispatch<React.SetStateAction<File | null>>;
	setSrcImg: React.Dispatch<React.SetStateAction<string | null>>;
}
const LIMIT = 12;
export const SearchImage: React.FC<Props> = ({
	srcImg,
	isCaseReadOnly,
	setValue,
	setFile,
	setSrcImg,
	sourceType,
	setSourceType,
}) => {
	const {
		Modal: ModalImageEditor,
		show: showModalImageEditor,
		hide: hideModalImageEditor,
	} = useModal();

	const { register, resetField } = useForm({
		mode: 'onSubmit',
	});
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [search, setSearch] = React.useState<string | undefined>();
	const [imageSelected, setImageSelected] = React.useState({
		image: '',
		index: -1,
	});
	const [imageToEdited, setImageToEdited] = React.useState({
		image: '',
		index: -1,
	});

	const [imagesListSearched, setImagesListSearched] = React.useState<
		FreepikImage[]
	>([]);
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [searchDebounce] = useDebounce(search, 500);

	const fetchData = async ({ pageParam = 1 }): Promise<MarketingImageData> => {
		const response = await freepikService.getSearchImages(
			searchDebounce ?? '',
			LIMIT,
			pageParam,
			true
		);
		return response.data;
	};

	const {
		data: listImages,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isLoading: isLoadingImages,
	} = useInfiniteQuery({
		queryKey: [GET_CAMPAIGNS_IMAGES, searchDebounce],
		queryFn: fetchData,
		initialPageParam: 1,
		refetchOnWindowFocus: false,
		enabled: !!searchDebounce,
		getNextPageParam: (lastPage) => {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (imagesListSearched.length > currentIndex) return;
		if (hasNextPage && !isFetchingNextPage) fetchNextPage();
	}, [currentIndex, hasNextPage, isFetchingNextPage]);

	React.useEffect(() => {
		if (listImages) {
			const listFlatMapped =
				listImages?.pages.flatMap((page) => page.items) || [];
			// const images = listFlatMapped.map((item) => item.image);

			if (search) {
				setImagesListSearched(listFlatMapped);
			} else {
				setImagesListSearched([]);
			}
		}
	}, [listImages, search]);

	React.useEffect(() => {
		if (sourceType !== 'url' && imageSelected.index >= 0) {
			clearSearch();
		}
	}, [srcImg]);

	const handleSelectListImg = (image: string, index: number): void => {
		setValue('file', image);
		setFile(null);
		setSrcImg(image);
		setSourceType('url');
		setImageSelected({ image, index });
		setValue('cover_image_url', image, { shouldDirty: true });
	};

	const handleDeleteImage = (): void => {
		setSrcImg(null);
		setFile(null);
		setValue('cover_image_url', '');
	};

	const handleImageEdited = (image: string) => {
		setValue('file', image);
		setFile(null);
		setSrcImg(image);
		setSourceType('url');
		setImageSelected({
			image: imageToEdited.image,
			index: imageToEdited.index,
		});
		setValue('cover_image_url', image, { shouldDirty: true });

		setImagesListSearched((prev) =>
			prev.map((itemImage, index) => {
				if (index === imageToEdited.index) {
					return {
						aspectRatio: itemImage.aspectRatio,
						id: itemImage.id,
						image: image,
						title: itemImage.title,
					};
				}
				return itemImage;
			})
		);
	};

	const clearSearch = (): void => {
		setImageSelected({
			index: -1,
			image: '',
		});
		setSearch(undefined);
		resetField('search');
	};

	const isFreePickImg = sourceType === 'url';

	const handleEditImage = async ({
		image,
		index,
	}: {
		image: FreepikImage;
		index: number;
	}): Promise<void> => {
		setImageToEdited({ image: image.image, index });
		setCurrentIndex(index);
		showModalImageEditor();
		try {
			const res = await freepikService.getFullFreepikImage(image.id);
			setImageToEdited({ image: res.data.url, index });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<>
			<ContainerImage title="Search Images" className="flex-1">
				<InputText
					name="search"
					register={register}
					iconLeft={Icons.search}
					iconRight={search ? Icons.closeCircle : undefined}
					className={clsx('w-full 2xl:!w-full', {})}
					classNameInput={clsx('!pl-12 !mt-0 placeholder:text-sm')}
					classNameIcon={clsx('!top-[30%] !size-5')}
					rightClick={clearSearch}
					onChangeCustom={(e: any) => setSearch(e.target.value)}
					placeholder="Describe the image you want search for"
					disabled={isCaseReadOnly}
				/>
				{imagesListSearched.length === 0 &&
					(!srcImg || (srcImg && !isFreePickImg)) && (
						<Image
							alt="no image"
							src={Icons.imageEmptyState}
							width={125}
							height={65}
							className={clsx('!my-12 mx-auto max-w-screen-3xl')}
							unoptimized
						/>
					)}
				{imagesListSearched.length === 0 && srcImg && isFreePickImg && (
					<div className="mt-4 flex items-center gap-4">
						<div className="relative">
							<ImageComponent
								image={srcImg}
								index={0}
								register={register}
								isSelectedByDefault={true}
								onClick={() =>
									handleEditImage({
										image: {
											aspectRatio: 0, // by default
											id: 0, // by default
											image: srcImg,
											title: '', // by default
										},
										index: 0,
									})
								}
								handleAddImage={() => handleSelectListImg(srcImg, 0)}
								imageSelected={imageSelected}
							/>
						</div>
						<button
							disabled={isCaseReadOnly}
							type="button"
							className="hover:opacity-60"
							onClick={handleDeleteImage}
						>
							<Icon
								src={Icons.trash}
								className="text-main-red"
								width={16}
								height={16}
							/>
						</button>
					</div>
				)}
				{imagesListSearched.length > 0 && (
					<ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-4 gap-y-3 3xl:gap-y-6 gap-x-2 max-h-[132px] overflow-y-auto scroll-custom mt-6 pr-3">
						{imagesListSearched.map((image, index) => {
							const isLastElement = imagesListSearched.length === index + 1;
							return (
								<div
									key={'image-searched-' + image + '-' + index}
									ref={isLastElement ? lastItemCallback : undefined}
								>
									<ImageComponent
										image={image.image}
										index={index}
										register={register}
										onClick={() => handleEditImage({ image, index })}
										handleAddImage={() =>
											handleSelectListImg(image.image, index)
										}
										imageSelected={imageSelected}
										className="2xl:w-[104px] 3xl:w-[124px]"
									/>
								</div>
							);
						})}
					</ul>
				)}
			</ContainerImage>

			<ModalImageEditor
				onClose={() => {
					hideModalImageEditor();
					setImageToEdited({ image: '', index: 0 });
				}}
				classNameCardContainer={'xl:!w-[1329px] !h-[732px]  !p-6'}
				classNameContainerChildren="!px-0"
				noMaxHeight
			>
				<ImageEditor
					image={imageToEdited.image}
					handleImageEdited={handleImageEdited}
					hasDownload
					hideImageEditor={hideModalImageEditor}
					aspectRatio={
						getAspectRatioByChannel['GOOGLE_ADS' as CampaignChannels]
					}
					width={getImageSizeByChannel['FACEBOOK' as CampaignChannels]?.width}
					height={getImageSizeByChannel['FACEBOOK' as CampaignChannels]?.height}
				/>
			</ModalImageEditor>
		</>
	);
};
